import {
    AfterContentChecked,
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
    ViewEncapsulation,
    Input, DestroyRef, inject
} from '@angular/core';
import {
    ɵdefineHiddenProp as defineHiddenProp,
    FieldType,
    FieldWrapper,
    FormlyConfig,
    FormlyFieldConfig
} from '@ngx-formly/core';
import { MatFormField } from '@angular/material/form-field';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

interface MatFormlyFieldConfig extends FormlyFieldConfig {
    _matprefix: TemplateRef<any>;
    _matsuffix: TemplateRef<any>;
    __formField__: FormlyHorizontalWrapperComponent;
}

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-formly-horizontal-wrapper',
    styleUrls: ['./formly-horizontal-wrapper.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: FormlyHorizontalWrapperComponent }],
    templateUrl: './formly-horizontal-wrapper.component.html',
    standalone: false
})

// Credit: ngx-formly
// Custom updates to https://github.com/ngx-formly/ngx-formly/blob/v5/src/material/form-field/src/form-field.wrapper.ts
export class FormlyHorizontalWrapperComponent extends FieldWrapper<MatFormlyFieldConfig>
    implements OnInit, OnDestroy, MatFormFieldControl<any>, AfterViewInit, AfterContentChecked {

    @ViewChild('fieldComponent', { read: ViewContainerRef, static: true }) fieldComponent!: ViewContainerRef;

    @ViewChild(MatFormField, { static: true }) formField!: MatFormField;

    @Input() hasLocalAttributes: boolean;

    _errorState = false;
    private initialGapCalculated = false;
    stateChanges = new Subject<void>();
    private readonly destroyRef: DestroyRef = inject(DestroyRef);


    constructor(
        private config: FormlyConfig,
        private elementRef: ElementRef,
        private focusMonitor: FocusMonitor,
        private renderer: Renderer2
    ) {
        super();
    }



    ngOnInit(): void {

        this.formField._control = this;
        defineHiddenProp(this.field, '__formField__', this.formField);

        const ref = this.config.resolveFieldTypeRef(this.formlyField);
        if (ref && !(ref.instance instanceof FieldType)) {
            console.warn(
                `Component '${ref.componentType.name}' must extend 'FieldType' from '@ngx-formly/material/form-field'.`
            );
        }

        // fix for https://github.com/angular/material2/issues/11437
        if (this.formlyField.hide && this.formlyField.templateOptions!.appearance === 'outline') {
            this.initialGapCalculated = true;
        }

        this.focusMonitor.monitor(this.elementRef, true)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((origin) => {
            if (!origin && this.field.focus) {
                this.field.focus = false;
            }

            this.stateChanges.next();
        });
    }



    ngAfterContentChecked(): void {
        if (!this.initialGapCalculated || this.formlyField.hide) {
            return;
        }
        // this.formField.updateOutlineGap();
        this.initialGapCalculated = true;
    }



    ngAfterViewInit(): void {
        // temporary fix for https://github.com/angular/material2/issues/7891
        if (this.formField.appearance !== 'outline' && this.to.hideFieldUnderline === true) {
            const underlineElement = this.formField._elementRef.nativeElement.querySelector(
                '.mat-form-field-underline'
            );
            underlineElement && this.renderer.removeChild(underlineElement.parentNode, underlineElement);
        }

        if (this.to.isDirty) {
            setTimeout(() => this.formControl.markAsDirty(), 0);
        }
    }



    ngOnDestroy():  void {
        delete this.formlyField.__formField__;
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this.elementRef);
    }



    setDescribedByIds(ids: string[]): void {}



    onContainerClick(event: MouseEvent): void {
        this.formlyField.focus = true;
        this.stateChanges.next();
    }



    removeLocalAttribute(): any {
        this.to.hidden = true;
        this.to.removeCallback(this.to.index);
    }


    editLocale(): void {
        this.formlyField.templateOptions.editLocales();
    }



    getInputCssClass(): string {
        if (this.to.scope === 'LOCAL') {
            return 'attr-local';
        }
        const hasChanged = this.formControl.dirty;
        if (this.to.isOverride || hasChanged) {
            return 'attr-global-overridden';
        }
        return 'attr-global';
    }



    get errorState(): any {
        const showError = this.options!.showError!(this);
        if (showError !== this._errorState) {
            this._errorState = showError;
            this.stateChanges.next();
        }

        return showError;
    }

    get controlType(): string {
        return this.to.type;
    }

    get focused(): boolean {
        return !!this.formlyField.focus && !this.disabled;
    }

    get disabled(): boolean {
        return !!this.to.disabled;
    }

    get required(): boolean {
        return !!this.to.required;
    }

    get placeholder(): any {
        return this.to.placeholder || '';
    }

    get shouldPlaceholderFloat(): boolean {
        return this.shouldLabelFloat;
    }

    get value(): string {
        return this.formControl.value;
    }

    get ngControl(): any {
        return this.formControl as any;
    }

    get empty(): boolean {
        return !this.formControl.value;
    }

    get shouldLabelFloat(): boolean {
        return this.focused || !this.empty;
    }

    get formlyField(): any {
        return this.field as MatFormlyFieldConfig;
    }

}
